// Webclient → Settings. Presence status, do-not-disturb, and 3-leg call
// forwarding (all / busy / no-answer) on /api/portal/me/*.

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useActiveTenant } from '../lib/tenantScope';

const STATUSES = ['Available', 'Away', 'Do Not Disturb', 'Lunch', 'Business Trip'];

type Fwd = {
  status: string; do_not_disturb: boolean;
  forward_all_enabled: boolean; forward_all_destination: string;
  forward_busy_enabled: boolean; forward_busy_destination: string;
  forward_no_answer_enabled: boolean; forward_no_answer_destination: string;
};

const BLANK: Fwd = {
  status: 'Available', do_not_disturb: false,
  forward_all_enabled: false, forward_all_destination: '',
  forward_busy_enabled: false, forward_busy_destination: '',
  forward_no_answer_enabled: false, forward_no_answer_destination: '',
};

function ForwardRow({ label, enabled, dest, onToggle, onDest }: {
  label: string; enabled: boolean; dest: string; onToggle: (v: boolean) => void; onDest: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <label className="flex items-center gap-2 w-40 shrink-0 text-sm">
        <input type="checkbox" checked={enabled} onChange={e => onToggle(e.target.checked)} />
        {label}
      </label>
      <input value={dest} onChange={e => onDest(e.target.value)} disabled={!enabled}
        placeholder="Destination (extension or number)"
        className="input py-1 text-sm flex-1 disabled:opacity-50" />
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const [form, setForm] = useState<Fwd>(BLANK);
  const [saved, setSaved] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const tenant = useActiveTenant();
  const settings = useQuery({ queryKey: ['portal-settings', tenant], queryFn: () => api.portal.settings() });
  useEffect(() => { if (settings.data) setForm({ ...BLANK, ...settings.data }); }, [settings.data]);

  const set = (patch: Partial<Fwd>) => setForm(f => ({ ...f, ...patch }));

  // Two-factor backup codes (#941): self-service regenerate, plaintext shown once.
  const [codes, setCodes] = useState<string[] | null>(null);
  const [codesErr, setCodesErr] = useState('');
  const [codesBusy, setCodesBusy] = useState(false);
  const regenCodes = async () => {
    if (!confirm('Generate new two-factor backup codes? Your old codes stop working immediately.')) return;
    setCodesBusy(true); setCodesErr('');
    try {
      const r = await api.mfaBackupCodes();
      setCodes(r.codes);
    } catch (e: any) {
      let msg = e?.message || 'could not generate backup codes';
      try { msg = JSON.parse(msg).error || msg; } catch { /* plain message */ }
      setCodesErr(msg);
    } finally { setCodesBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(''); setSaved('');
    try {
      await api.portal.saveStatus({ status: form.status, do_not_disturb: form.do_not_disturb });
      await api.portal.saveForwarding(form);
      qc.invalidateQueries({ queryKey: ['portal-settings'] });
      setSaved('Saved');
      setTimeout(() => setSaved(''), 2500);
    } catch (e: any) { setErr(e.message || 'save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-1">Settings</h2>
          <p className="text-textSecondary text-sm">Your presence and call handling.</p>
        </div>

        <div className="card space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select className="input" value={form.status} onChange={e => set({ status: e.target.value })}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.do_not_disturb} onChange={e => set({ do_not_disturb: e.target.checked })} />
            Do not disturb (send calls straight to voicemail)
          </label>
        </div>

        <div className="card">
          <div className="text-sm font-medium mb-2">Call forwarding</div>
          <ForwardRow label="Always" enabled={form.forward_all_enabled} dest={form.forward_all_destination}
            onToggle={v => set({ forward_all_enabled: v })} onDest={v => set({ forward_all_destination: v })} />
          <ForwardRow label="When busy" enabled={form.forward_busy_enabled} dest={form.forward_busy_destination}
            onToggle={v => set({ forward_busy_enabled: v })} onDest={v => set({ forward_busy_destination: v })} />
          <ForwardRow label="No answer" enabled={form.forward_no_answer_enabled} dest={form.forward_no_answer_destination}
            onToggle={v => set({ forward_no_answer_enabled: v })} onDest={v => set({ forward_no_answer_destination: v })} />
        </div>

        <div className="card">
          <div className="text-sm font-medium mb-1">Two-factor backup codes</div>
          <p className="text-textSecondary text-sm mb-3">One-time codes that sign you in if you lose your authenticator. Generating a new set replaces the old one immediately.</p>
          {codes ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-sm">
                {codes.map(c => <div key={c}>{c}</div>)}
              </div>
              <p className="text-textSecondary text-xs">Store these somewhere safe — they will not be shown again.</p>
            </div>
          ) : (
            <button onClick={regenCodes} disabled={codesBusy} className="btn-primary px-4 py-2 text-sm disabled:opacity-50">
              {codesBusy ? 'Generating…' : 'Generate new backup codes'}
            </button>
          )}
          {codesErr && <div className="text-danger text-sm mt-2">{codesErr}</div>}
        </div>

        <div className="flex items-center gap-3">
          <button onClick={save} disabled={busy || settings.isLoading} className="btn-primary px-5 py-2 text-sm disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
          {saved && <span className="text-success text-sm">{saved}</span>}
          {err && <span className="text-danger text-sm">{err}</span>}
        </div>
      </div>
    </div>
  );
}
